import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Alert,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Search from "@mui/icons-material/Search";
import { SmsOutlined, CalendarMonth, EmojiEventsOutlined, TrendingUp } from "@mui/icons-material";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import ChartCard from "@/components/data-visuals/ChartCard";
import SFDateRangeSelector from "@/components/inputs/SFDateRangeSelector";
import { PHARMACY_HALO_COLORS } from "@/config/colors";
import { getChemistName } from "@/utils/textUtils";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import AdminPharmacyHaloStatCard from "@/components/admin/AdminPharmacyHaloStatCard";
import AdminBoolCell from "@/components/admin/AdminBoolCell";
import { pickFirst } from "@/utils/adminDashboardUtils";
import {
  adminDateRangeBlueBoxSx,
  pharmacyHaloDataGridSx,
} from "@/utils/adminTableStyles";
import {
  formatGridDateTime,
  gridDateTimeSortValue,
} from "@/utils/gridDateTimeFormat";

/** Fill missing days; API uses `date` and `totalSms`. */
const preprocessSmsSeries = (rows, dateStart, dateEnd) => {
  if (!rows || !Array.isArray(rows)) return [];
  const start = new Date(dateStart);
  const end = new Date(dateEnd);
  const map = new Map();
  rows.forEach((r) => {
    const raw = r.date;
    if (!raw) return;
    const key = new Date(raw).toISOString().split("T")[0];
    const n = Number(r.totalSms ?? 0);
    map.set(key, (map.get(key) || 0) + n);
  });
  const out = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const key = d.toISOString().split("T")[0];
    out.push({ Day: key, SmsCount: map.get(key) || 0 });
  }
  return out;
};

const AdminHaloTextPage = ({ isManager = false }) => {
  const navigate = useNavigate();
  const chemistBasePath = isManager ? "/manager-functions/chemist" : "/admin-functions/chemist";
  const [data, setData] = useState(null);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [tableFilter, setTableFilter] = useState("");
  const [usageChartOpen, setUsageChartOpen] = useState(false);

  useEffect(() => {
    const today = new Date();
    const fourWeeksAgo = new Date();
    fourWeeksAgo.setDate(today.getDate() - 28);
    setDateStart(fourWeeksAgo.toISOString().split("T")[0]);
    setDateEnd(today.toISOString().split("T")[0]);
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const params = { DateStart: dateStart, DateEnd: dateEnd };
      const res = await phappsRequest(
        isManager ? API_PATHS.MANAGER_FUNCTIONS_HALOTEXT : API_PATHS.ADMIN_FUNCTIONS_HALOTEXT,
        "GET",
        null,
        params
      );
      setData(res);
      setFetchError(null);
    } catch (e) {
      console.error(e);
      setData(null);
      setFetchError(getAdminApiErrorMessage(e, "Failed to load HaloText data"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateStart && dateEnd) load();
  }, [dateStart, dateEnd]);

  const summary = data?.Summary || {};
  const smsSeries = useMemo(
    () => preprocessSmsSeries(data?.SmsSentOverTime || [], dateStart, dateEnd),
    [data, dateStart, dateEnd]
  );

  const totalSms = Number(
    pickFirst(summary, ["TotalSmsSent", "totalSmsSent", "TotalSms"]) ?? 0
  );
  const smsLast30 = Number(
    pickFirst(summary, [
      "SmsSentLast30Days",
      "TotalSmsSentIn30Days",
      "SmsLast30Days",
      "smsLast30Days",
    ]) ?? 0
  );

  const topChem = summary.HighestSmsChemist;
  const topChem30d = summary.HighestSmsChemist30d;

  const chemRows = data?.ChemistTable || [];
  const gridRows = (chemRows || []).map((row, i) => {
    const dedicated = row?.SMSDedicatedNumber ?? row?.SmsDedicatedNumber;
    const hasBroadcast =
      dedicated != null && String(dedicated).trim() !== "";
    const smsLast30 = pickFirst(row, [
      "TotalSmsSentIn30Days",
      "SmsSentLast30Days",
      "smsSentLast30Days",
    ]);
    return {
      id: row.ChemistUUID || `r-${i}`,
      chemistUUID: row.ChemistUUID ?? "",
      chemistName: row.ChemistName,
      totalSms: row.TotalSmsSent ?? 0,
      smsIn30Days:
        smsLast30 != null && smsLast30 !== ""
          ? Number(smsLast30)
          : null,
      totalStaff: row.TotalStaff ?? null,
      latestActivity: row.LatestActivity ?? "—",
      broadcastActive: hasBroadcast,
    };
  });

  const filteredRows = useMemo(() => {
    const q = tableFilter.trim().toLowerCase();
    if (!q) return gridRows;
    return gridRows.filter((r) =>
      String(getChemistName(r.chemistName) || "")
        .toLowerCase()
        .includes(q)
    );
  }, [gridRows, tableFilter]);

  const dedicatedNumberPie = useMemo(() => {
    if (!gridRows.length) {
      return [
        { x: "Has Dedicated Number", y: 0, color: PHARMACY_HALO_COLORS.BLUE },
        { x: "PH Number", y: 0, color: PHARMACY_HALO_COLORS.YELLOW },
      ];
    }
    const on = gridRows.filter((r) => r.broadcastActive).length;
    const off = gridRows.length - on;
    const slices = [
      { x: "Has Dedicated Number", y: on, color: PHARMACY_HALO_COLORS.BLUE },
      { x: "PH Number", y: off, color: PHARMACY_HALO_COLORS.YELLOW },
    ];
    return slices.some((s) => s.y > 0)
      ? slices.filter((s) => s.y > 0)
      : slices;
  }, [gridRows]);

  const columns = [
    {
      field: "chemistName",
      headerName: "Chemist Name",
      flex: 1,
      minWidth: 200,
      renderCell: (p) => (
        <Typography
          variant="body2"
          onClick={() => p.row.chemistUUID && navigate(`${chemistBasePath}/${encodeURIComponent(p.row.chemistUUID)}`, { state: { tab: 3 } })}
          sx={{ cursor: p.row.chemistUUID ? "pointer" : "default", fontWeight: 500, "&:hover": { textDecoration: p.row.chemistUUID ? "underline" : "none" } }}
        >
          {getChemistName(p.value)}
        </Typography>
      ),
    },
    {
      field: "broadcastActive",
      headerName: "Dedicated number",
      width: 110,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => <AdminBoolCell value={p.value} />,
    },
    {
      field: "totalSms",
      headerName: "Total SMS sent",
      width: 130,
      type: "number",
    },
    {
      field: "smsIn30Days",
      headerName: "SMS (30d)",
      width: 110,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (_v, row) =>
        row.smsIn30Days != null && Number.isFinite(row.smsIn30Days)
          ? row.smsIn30Days
          : -1,
      renderCell: (p) =>
        p.row.smsIn30Days != null && Number.isFinite(p.row.smsIn30Days)
          ? Number(p.row.smsIn30Days).toLocaleString()
          : "—",
    },
    {
      field: "totalStaff",
      headerName: "Staff",
      width: 90,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (_v, row) =>
        row.totalStaff != null && Number.isFinite(row.totalStaff)
          ? row.totalStaff
          : -1,
      renderCell: (p) =>
        p.row.totalStaff != null && Number.isFinite(p.row.totalStaff)
          ? Number(p.row.totalStaff).toLocaleString()
          : "—",
    },
    {
      field: "latestActivity",
      headerName: "Latest activity",
      flex: 1,
      minWidth: 170,
      valueGetter: (_value, row) => {
        const t = gridDateTimeSortValue(row.latestActivity);
        return t == null ? Number.MAX_SAFE_INTEGER : t;
      },
      renderCell: (p) => formatGridDateTime(p.row.latestActivity),
    },
    {
      field: "actions",
      headerName: "Open",
      width: 100,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => (
        <Button
          size="small"
          variant="contained"
          disabled={!p.row.chemistUUID}
          onClick={() =>
            navigate(
              `${chemistBasePath}/${encodeURIComponent(p.row.chemistUUID)}`,
              { state: { tab: 3 } }
            )
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            backgroundColor: PHARMACY_HALO_COLORS.BLUE,
            minWidth: 68,
          }}
        >
          Open
        </Button>
      ),
    },
  ];

  const chemSubtitle =
    topChem?.ChemistName != null ? getChemistName(topChem.ChemistName) : undefined;
  const chemValue = Number(topChem?.TotalSmsSent ?? 0).toLocaleString();

  const topChem30dSubtitle =
    topChem30d?.ChemistName != null ? getChemistName(topChem30d.ChemistName) : undefined;
  const topChem30dValue = Number(topChem30d?.TotalSmsSentIn30Days ?? 0).toLocaleString();

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <div className="p-4 space-y-4 w-full max-w-full">
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ color: PHARMACY_HALO_COLORS.BLUE }}
        >
          Admin HaloText overview
        </Typography>
        {fetchError ? (
          <Alert severity="warning">{fetchError}</Alert>
        ) : null}

        {loading && !data ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading HaloText data…</p>
            </div>
          </div>
        ) : (
          <>
            <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
              <Box sx={adminDateRangeBlueBoxSx}>
                <SFDateRangeSelector
                  adminToolbar
                  value={[new Date(dateStart), new Date(dateEnd)]}
                  onChange={(range) => {
                    setDateStart(range[0]?.toISOString().split("T")[0]);
                    setDateEnd(range[1]?.toISOString().split("T")[0]);
                  }}
                />
              </Box>

              <div className="min-w-0">
                <AdminPharmacyHaloStatCard
                  title="Total SMS"
                  value={totalSms}
                  valueFormatter={(v) => Number(v || 0).toLocaleString()}
                  Icon={SmsOutlined}
                />
              </div>

              <div className="min-w-0">
                <AdminPharmacyHaloStatCard
                  title="SMS in last 30 days"
                  value={smsLast30}
                  valueFormatter={(v) => Number(v || 0).toLocaleString()}
                  Icon={CalendarMonth}
                />
              </div>

              <div className="min-w-0">
                <AdminPharmacyHaloStatCard
                  title="Highest SMS chemist (total)"
                  subtitle={chemSubtitle}
                  value={topChem?.ChemistName != null ? chemValue : "—"}
                  Icon={EmojiEventsOutlined}
                />
              </div>

              <div className="min-w-0">
                <AdminPharmacyHaloStatCard
                  title="Highest SMS chemist (30d)"
                  subtitle={topChem30dSubtitle}
                  value={topChem30d?.ChemistName != null ? topChem30dValue : "—"}
                  Icon={TrendingUp}
                />
              </div>
            </section>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  borderColor: PHARMACY_HALO_COLORS.BLUE,
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      fontWeight={700}
                      sx={{ color: PHARMACY_HALO_COLORS.BLUE }}
                    >
                      Chemists
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setUsageChartOpen(true)}
                        sx={{
                          textTransform: "none",
                          borderColor: PHARMACY_HALO_COLORS.BLUE,
                          color: PHARMACY_HALO_COLORS.BLUE,
                          fontWeight: 600,
                        }}
                      >
                        See SMS over time
                      </Button>
                      <TextField
                        size="small"
                        placeholder="Filter by chemist name"
                        value={tableFilter}
                        onChange={(e) => setTableFilter(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ minWidth: 260 }}
                      />
                    </Box>
                  </Box>
                  <Divider />
                  <div
                    style={{ height: 520, width: "100%", padding: "0 8px 16px" }}
                  >
                    <DataGrid
                      rows={filteredRows}
                      columns={columns}
                      loading={loading}
                      pageSizeOptions={[10, 20, 50]}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 20 } },
                      }}
                      disableRowSelectionOnClick
                      sx={pharmacyHaloDataGridSx}
                    />
                  </div>
                </CardContent>
              </Card>

              <Box
                sx={{
                  width: { xs: "100%", lg: 380 },
                  flexShrink: 0,
                  minHeight: 400,
                  "& > div": { marginTop: "0 !important" },
                }}
              >
                <ChartCard
                  title=""
                  hideMainChart
                  pieChartMinHeight={400}
                  data={{}}
                  primaryXAxis={{}}
                  primaryYAxis={{}}
                  borderColor={PHARMACY_HALO_COLORS.BLUE}
                  series={[]}
                  pieData={dedicatedNumberPie}
                  pieTitle="Dedicated Numbers"
                />
              </Box>
            </Box>

          <Dialog
            open={usageChartOpen}
            onClose={() => setUsageChartOpen(false)}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle sx={{ color: PHARMACY_HALO_COLORS.BLUE, fontWeight: 700 }}>
              SMS sent over time
            </DialogTitle>
            <DialogContent>
              <Box sx={{ "& > div": { mt: 0 }, pt: 1 }}>
                <ChartCard
                  title=""
                  hidePieChart
                  mainChartMinHeight={360}
                  data={{ sms: smsSeries }}
                  primaryXAxis={{
                    valueType: "DateTime",
                    labelFormat: "dd/MM",
                    intervalType: "Days",
                  }}
                  primaryYAxis={{
                    title: "SMS",
                    minimum: 0,
                    labelFormat: "{value}",
                  }}
                  borderColor={PHARMACY_HALO_COLORS.BLUE}
                  series={[
                    {
                      dataSource: smsSeries,
                      xName: "Day",
                      yName: "SmsCount",
                      type: "Line",
                      name: "SMS sent",
                      width: 2,
                      fill: PHARMACY_HALO_COLORS.BLUE,
                      marker: { visible: true, width: 8, height: 8 },
                    },
                  ]}
                  pieData={[]}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setUsageChartOpen(false)} color="primary">
                Close
              </Button>
            </DialogActions>
          </Dialog>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminHaloTextPage;
